import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  DataTable,
  Modal,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

const MACROCATEGORIE = [
  { value: "100000000", label: "Manutenzione" },
  { value: "100000001", label: "Servizi" },
  { value: "100000002", label: "Forniture" },
  { value: "100000003", label: "Consulenza" },
];

const TIPOLOGIE_CRITERIO = [
  { value: "100000000", label: "Automatico" },
  { value: "100000001", label: "Manuale" },
];

const FORMULE_DISPONIBILI = [
  { value: "intervention_timeliness", label: "Puntualità interventi" },
  { value: "rapportino_completeness", label: "Completezza rapportini" },
  { value: "completion_rate", label: "Tasso completamento scadenze" },
  { value: "corrective_actions", label: "Azioni correttive" },
  { value: "response_time", label: "Tempo medio risposta" },
  { value: "contract_compliance", label: "Conformità contrattuale" },
  { value: "document_completeness", label: "Completezza documentale" },
];

function CriteriValutazionePage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();

  const [criteri, setCriteri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterMacrocategoria, setFilterMacrocategoria] = useState("");

  const [formData, setFormData] = useState({
    nome: "",
    descrizione: "",
    macrocategoria: "",
    tipologia: "100000001",
    peso: "5",
    formulaAutomatica: "",
    attivo: true,
    ordine: "0",
  });

  const isAdmin = user?.ruolo === 100000000;

  useEffect(() => {
    loadData();
  }, [filterMacrocategoria]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMacrocategoria) params.macrocategoria = filterMacrocategoria;
      const data = await apiRequest(
        `/criteri-valutazione${Object.keys(params).length ? "?" + new URLSearchParams(params) : ""}`,
      );
      setCriteri(data || []);
    } catch (error) {
      console.error("Error loading criteri:", error);
      showToast("Errore nel caricamento dei criteri", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      descrizione: "",
      macrocategoria: "",
      tipologia: "100000001",
      peso: "5",
      formulaAutomatica: "",
      attivo: true,
      ordine: "0",
    });
  };

  const openCreate = () => {
    resetForm();
    setEditingItem(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nome: item.phc_name || "",
      descrizione: item.phc_descrizione || "",
      macrocategoria: String(item.phc_macrocategoria ?? ""),
      tipologia: String(item.phc_tipologia ?? "100000001"),
      peso: String(item.phc_peso ?? "5"),
      formulaAutomatica: item.phc_formulaautomatica || "",
      attivo: item.phc_attivo !== false,
      ordine: String(item.phc_ordine ?? "0"),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome || !formData.macrocategoria || !formData.peso) {
      showToast("Compila i campi obbligatori", "error");
      return;
    }

    if (formData.tipologia === "100000000" && !formData.formulaAutomatica) {
      showToast(
        "Per criteri automatici è necessario selezionare una formula",
        "error",
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: formData.nome,
        descrizione: formData.descrizione,
        macrocategoria: formData.macrocategoria,
        tipologia: formData.tipologia,
        peso: formData.peso,
        formulaAutomatica:
          formData.tipologia === "100000000"
            ? formData.formulaAutomatica
            : null,
        attivo: formData.attivo,
        ordine: formData.ordine,
      };

      if (editingItem) {
        await apiRequest(
          `/criteri-valutazione/${editingItem.phc_criterivalutazioneid}`,
          { method: "PUT", body: JSON.stringify(payload) },
        );
        showToast("Criterio aggiornato", "success");
      } else {
        await apiRequest("/criteri-valutazione", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        showToast("Criterio creato", "success");
      }

      setModalOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving criterio:", error);
      showToast(error.message || "Errore nel salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await apiRequest(
        `/criteri-valutazione/${itemToDelete.phc_criterivalutazioneid}`,
        { method: "DELETE" },
      );
      showToast("Criterio eliminato", "success");
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      showToast(error.message || "Errore nell'eliminazione", "error");
    }
  };

  const getMacrocategoriaLabel = (value) => {
    const found = MACROCATEGORIE.find((m) => m.value === String(value));
    return found ? found.label : "-";
  };

  const getTipologiaLabel = (value) => {
    return value === 100000000 ? "Automatico" : "Manuale";
  };

  // Stats
  const stats = {
    totale: criteri.length,
    attivi: criteri.filter((c) => c.phc_attivo).length,
    automatici: criteri.filter((c) => c.phc_tipologia === 100000000).length,
    manuali: criteri.filter((c) => c.phc_tipologia === 100000001).length,
  };

  const columns = [
    {
      key: "phc_name",
      label: "Nome",
      sortable: true,
      render: (row) => (
        <div>
          <span className="font-medium">{row.phc_name}</span>
          {!row.phc_attivo && (
            <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
              Inattivo
            </span>
          )}
        </div>
      ),
    },
    {
      key: "phc_macrocategoria",
      label: "Macrocategoria",
      sortable: true,
      render: (row) => (
        <span className="text-sm px-2 py-0.5 rounded bg-blue-50 text-blue-700">
          {getMacrocategoriaLabel(row.phc_macrocategoria)}
        </span>
      ),
    },
    {
      key: "phc_tipologia",
      label: "Tipo",
      sortable: true,
      render: (row) => (
        <span
          className={`text-xs px-2 py-0.5 rounded ${
            row.phc_tipologia === 100000000
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {getTipologiaLabel(row.phc_tipologia)}
        </span>
      ),
    },
    {
      key: "phc_peso",
      label: "Peso",
      sortable: true,
      render: (row) => <span className="font-mono">{row.phc_peso}/10</span>,
    },
    {
      key: "phc_ordine",
      label: "Ordine",
      sortable: true,
    },
    {
      key: "actions",
      label: "Azioni",
      render: (row) =>
        isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
              Modifica
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                setItemToDelete(row);
                setDeleteConfirmOpen(true);
              }}
            >
              Elimina
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Criteri Valutazione Fornitori
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestione catalogo criteri per valutazione ISO 9001:2015
          </p>
        </div>
        {isAdmin && <Button onClick={openCreate}>+ Nuovo Criterio</Button>}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{stats.totale}</p>
          <p className="text-sm text-gray-500">Totale Criteri</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.attivi}</p>
          <p className="text-sm text-gray-500">Attivi</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.automatici}</p>
          <p className="text-sm text-gray-500">Automatici</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.manuali}</p>
          <p className="text-sm text-gray-500">Manuali</p>
        </Card>
      </div>

      {/* Filter */}
      <Card className="p-4">
        <div className="flex gap-4 items-end">
          <div className="w-64">
            <Select
              label="Filtra per Macrocategoria"
              value={filterMacrocategoria}
              onChange={(e) => setFilterMacrocategoria(e.target.value)}
              options={[{ value: "", label: "Tutte" }, ...MACROCATEGORIE]}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={columns}
          data={criteri}
          loading={loading}
          emptyMessage="Nessun criterio configurato"
          searchable
          searchPlaceholder="Cerca criterio..."
          searchKeys={["phc_name", "phc_descrizione"]}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingItem ? "Modifica Criterio" : "Nuovo Criterio"}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nome Criterio *"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            placeholder="es. Puntualità interventi"
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Macrocategoria *"
              value={formData.macrocategoria}
              onChange={(e) =>
                setFormData({ ...formData, macrocategoria: e.target.value })
              }
              options={[
                { value: "", label: "-- Seleziona --" },
                ...MACROCATEGORIE,
              ]}
            />
            <Select
              label="Tipologia *"
              value={formData.tipologia}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  tipologia: e.target.value,
                  formulaAutomatica:
                    e.target.value === "100000001"
                      ? ""
                      : formData.formulaAutomatica,
                })
              }
              options={TIPOLOGIE_CRITERIO}
            />
          </div>

          {formData.tipologia === "100000000" && (
            <Select
              label="Formula Automatica *"
              value={formData.formulaAutomatica}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  formulaAutomatica: e.target.value,
                })
              }
              options={[
                { value: "", label: "-- Seleziona formula --" },
                ...FORMULE_DISPONIBILI,
              ]}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Peso (1-10) *"
              type="number"
              min="1"
              max="10"
              value={formData.peso}
              onChange={(e) =>
                setFormData({ ...formData, peso: e.target.value })
              }
            />
            <Input
              label="Ordine"
              type="number"
              min="0"
              value={formData.ordine}
              onChange={(e) =>
                setFormData({ ...formData, ordine: e.target.value })
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              rows={3}
              value={formData.descrizione}
              onChange={(e) =>
                setFormData({ ...formData, descrizione: e.target.value })
              }
              placeholder="Descrizione del criterio e come deve essere valutato..."
            />
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.attivo}
              onChange={(e) =>
                setFormData({ ...formData, attivo: e.target.checked })
              }
              className="rounded border-gray-300 text-blue-600"
            />
            <span className="text-sm text-gray-700">Attivo</span>
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving
                ? "Salvataggio..."
                : editingItem
                  ? "Salva Modifiche"
                  : "Crea Criterio"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Criterio"
        message={`Sei sicuro di voler eliminare il criterio "${itemToDelete?.phc_name}"?`}
        confirmText="Elimina"
        variant="danger"
      />
    </div>
  );
}

export default CriteriValutazionePage;
